import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ClPopper from '../ClPopper';
import ClTypography from '../ClTypography';
import { ClTooltipPlacement, ClTooltipTrigger } from './ClTooltip.types';

@Component({
  components: {
    ClPopper,
    ClTypography
  }
})
export default class ClTooltip extends Vue {
  /**
   * If true, the arrow will be visible.
   */
  @Prop({
    type: Boolean,
    default: true
  })
  public arrow!: boolean;

  /**
   * If true, the component will only render the default slot.
   */
  @Prop(Boolean)
  public disabled!: boolean;

  /**
   * Do not respond to focus events.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public disableFocusListener!: boolean;

  /**
   * Do not respond to hover events.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public disableHoverListener!: boolean;

  /**
   * Do not respond to long press touch events.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public disableTouchListener!: boolean;

  /**
   * The number of milliseconds to wait before showing
   * the tooltip. This property won't impact the enter
   * touch delay (enterTouchDelay).
   */
  @Prop({
    type: Number,
    default: 400
  })
  public enterDelay!: number;

  /**
   * The number of milliseconds a user must touch the element before showing the tooltip.
   */
  @Prop({
    type: Number,
    default: 1000
  })
  public enterTouchDelay!: number;

  /**
   * If true, the tooltip will always be visible.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public forceShow!: boolean;

  /**
   * The relationship between the tooltip and the wrapper component is not clear from the DOM.
   * This property is used with aria-describedby to solve the accessibility issue.
   */
  @Prop({
    type: String
  })
  public id!: string;

  /**
   * The number of milliseconds to wait before hiding the tooltip.
   * This property won't impact the leave touch delay (leaveTouchDelay).
   */
  @Prop({
    type: Number,
    default: 100
  })
  public leaveDelay!: number;

  /**
   * The number of milliseconds after the user stops touching an element before hiding the tooltip.
   */
  @Prop({
    type: Number,
    default: 1500
  })
  public leaveTouchDelay!: number;

  /**
   * The maximum width of the tooltip
   */
  @Prop({
    type: String
  })
  public maxWidth!: string;

  /**
   * The minimum width of the tooltip
   */
  @Prop({
    type: String
  })
  public minWidth!: string;

  /**
   * If true, the tooltip is shown.
   */
  @Prop(Boolean)
  public open!: boolean;

  /**
   * Tooltip placement
   */
  @Prop({
    type: String as () => ClTooltipPlacement,
    default: 'auto'
  })
  public placement!: ClTooltipPlacement;

  @Prop({
    type: String,
    default: ''
  })
  public title!: string;

  @Prop({
    type: String as () => ClTooltipTrigger,
    default: 'hover'
  })
  public trigger!: ClTooltipTrigger;

  @Prop({
    type: String
  })
  public transition!: string;

  @Prop({
    type: String
  })
  public transitionDuration!: string;

  /**
   * If `true` tooltip text will wrap
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public wrap!: boolean;

  get isDisabled(): boolean {
    return this.disabled || !Boolean(this.$slots.tooltip || this.title.length);
  }
}
